import type {
  BibleEntry,
  BibleImage,
  BibleAudio,
  BibleLink,
  BibleReference,
  BibleSection,
  ReferenceTarget,
  StoryBible,
  StoryCharacter,
  StoryProject,
} from './types'

export const blankBible = (): StoryBible => ({
  premise: '',
  world: '',
  notes: '',
  characters: [],
})
export const newId = () => crypto.randomUUID()
export function bibleSections(project: StoryProject): BibleSection[] {
  return [
    { id: 'locations', title: 'Locations' },
    ...(project.bible?.sections || []).filter((s) => s.id !== 'locations'),
  ]
}
export function referenceTargets(project: StoryProject): ReferenceTarget[] {
  return [
    ...(project.bible?.characters || []).map((c) => ({
      id: 'character:' + c.id,
      title: c.name || 'Unnamed character',
      section: 'Characters',
      kind: 'character' as const,
    })),
    ...(project.bible?.entries || []).map((e) => ({
      id: 'entry:' + e.id,
      title: e.title || 'Untitled entry',
      section:
        bibleSections(project).find((s) => s.id === e.sectionId)?.title ||
        'Bible',
      kind: 'entry' as const,
    })),
    ...project.nodes.map((n) => ({
      id: 'scene:' + n.id,
      title: n.title,
      section: 'Scenes',
      kind: 'scene' as const,
    })),
  ]
}
export function connectionTargets(project: StoryProject, sourceId: string, query = '') {
  const fold = (value: string) => value.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase()
  const words = fold(query).trim().split(/\s+/).filter(Boolean)
  return referenceTargets(project).filter(target => target.id !== sourceId &&
    words.every(word => fold(target.title + ' ' + target.section).includes(word)))
}

export function reorderConnection(project: StoryProject, sourceId: string, movingId: string, targetId: string): StoryProject {
  const links = project.bible?.links || []
  const belongs = (link: BibleLink) => link.sourceId === sourceId || link.targetId === sourceId
  const visible = links.filter(belongs)
  const from = visible.findIndex(link => link.id === movingId), to = visible.findIndex(link => link.id === targetId)
  if (from < 0 || to < 0 || from === to) return project
  visible.splice(to, 0, visible.splice(from, 1)[0])
  // Keep unrelated links in their slots and preserve every link's ID and data.
  // The existing ordered, shared link array handles saves, undo and live sync.
  let index = 0
  return { ...project, bible: { ...project.bible!, links: links.map(link => belongs(link) ? visible[index++] : link) } }
}

export function addConnection(
  project: StoryProject,
  sourceId: string,
  targetId: string,
  relationship = 'mentions',
) {
  if (
    sourceId === targetId ||
    !referenceTargets(project).some((t) => t.id === targetId)
  )
    return project
  const bible = project.bible || blankBible()
  if (
    (bible.links || []).some(
      (l) =>
        l.sourceId === sourceId &&
        l.targetId === targetId &&
        l.relationship === relationship,
    )
  )
    return project
  return {
    ...project,
    bible: {
      ...bible,
      links: [
        ...(bible.links || []),
        { id: newId(), sourceId, targetId, relationship },
      ],
    },
  }
}
export type ReferencePick = { targetId: string; entry?: BibleEntry }
export function applyReference(
  project: StoryProject,
  sourceId: string,
  pick?: ReferencePick,
) {
  if (!pick) return project
  if (pick.entry) {
    const bible = project.bible || blankBible()
    project = {
      ...project,
      bible: { ...bible, entries: [...(bible.entries || []), pick.entry] },
    }
  }
  return addConnection(project, sourceId, pick.targetId)
}
export function createBibleEntry(
  title: string,
  sectionId = 'locations',
): BibleEntry {
  return { id: newId(), sectionId, title, summary: '', body: '' }
}

export type ReferenceMention = {
  start: number
  end: number
  title: string
  targetIds: string[]
}

// Match the longest known name first, even when that longer entry is not linked
// here. A link to House A must never silently capture @House A Annex or email text.
export function createReferenceMatcher(project: StoryProject) {
  const names = new Map<string, string[]>()
  for (const target of referenceTargets(project)) {
    if (!target.title.trim()) continue
    names.set(target.title, [...(names.get(target.title) || []), target.id])
  }
  const linked = new Map<string, Set<string>>()
  for (const link of project.bible?.links || []) {
    const ids = linked.get(link.sourceId) || new Set<string>()
    ids.add(link.targetId)
    linked.set(link.sourceId, ids)
  }
  const pattern = [...names.keys()]
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')
  return (sourceId: string, text: string): ReferenceMention[] => {
    const targets = linked.get(sourceId)
    if (!pattern || !targets?.size || !text.includes('@')) return []
    const regex = new RegExp(
      '(^|[^\\p{L}\\p{N}_@])@(' + pattern + ')(?![\\p{L}\\p{N}_])',
      'gu',
    )
    return [...text.matchAll(regex)].flatMap((match) => {
      const targetIds = names.get(match[2])!.filter((id) => targets.has(id))
      if (!targetIds.length) return []
      const start = match.index! + match[1].length
      return [
        {
          start,
          end: start + match[2].length + 1,
          title: match[2],
          targetIds,
        },
      ]
    })
  }
}

export function referenceMentions(
  project: StoryProject,
  sourceId: string,
  text: string,
) {
  return createReferenceMatcher(project)(sourceId, text)
}

function replaceMentions(
  text: string,
  mentions: ReferenceMention[],
  value: (mention: ReferenceMention) => string,
) {
  return mentions.reduceRight(
    (result, mention) =>
      result.slice(0, mention.start) +
      value(mention) +
      result.slice(mention.end),
    text,
  )
}

export function renameReference(
  project: StoryProject,
  targetId: string,
  title: string,
): StoryProject {
  const target = referenceTargets(project).find((t) => t.id === targetId)
  if (!target || !title.trim() || target.title === title) return project
  const bible = project.bible || blankBible()
  const sources = new Set(
    (bible.links || [])
      .filter((l) => l.targetId === targetId)
      .map((l) => l.sourceId),
  )
  const match = createReferenceMatcher(project)
  const replace = (sourceId: string, text: string) =>
    replaceMentions(
      text,
      match(sourceId, text).filter(
        (m) => m.targetIds.length === 1 && m.targetIds[0] === targetId,
      ),
      () => '@' + title,
    )
  const uniqueSpeaker =
    target.kind === 'character' &&
    bible.characters.filter(
      (c) =>
        c.name.trim().toLowerCase() === target.title.trim().toLowerCase(),
    ).length === 1
  return {
    ...project,
    bible: {
      ...bible,
      characters: bible.characters.map((c) => ({
        ...c,
        ...(targetId === 'character:' + c.id ? { name: title } : {}),
        ...(sources.has('character:' + c.id)
          ? {
              description: replace('character:' + c.id, c.description),
              voice: replace('character:' + c.id, c.voice),
              role: replace('character:' + c.id, c.role),
            }
          : {}),
      })),
      ...(bible.entries
        ? {
            entries: bible.entries.map((e) => ({
              ...e,
              ...(targetId === 'entry:' + e.id ? { title } : {}),
              ...(sources.has('entry:' + e.id)
                ? {
                    body: replace('entry:' + e.id, e.body),
                    summary: replace('entry:' + e.id, e.summary),
                  }
                : {}),
            })),
          }
        : {}),
    },
    nodes: project.nodes.map((n) => ({
      ...n,
      ...(targetId === 'scene:' + n.id ? { title } : {}),
      blocks: n.blocks.map((b) => ({
        ...b,
        ...(uniqueSpeaker &&
        b.kind === 'dialogue' &&
        b.speaker?.trim().toLowerCase() === target.title.trim().toLowerCase()
          ? { speaker: title }
          : {}),
        ...(sources.has('scene:' + n.id)
          ? {
              text: replace('scene:' + n.id, b.text),
              ...(b.direction !== undefined
                ? { direction: replace('scene:' + n.id, b.direction) }
                : {}),
            }
          : {}),
      })),
      ...(sources.has('scene:' + n.id)
        ? {
            ...(n.notes !== undefined
              ? { notes: replace('scene:' + n.id, n.notes) }
              : {}),
            ...(n.synopsis !== undefined
              ? { synopsis: replace('scene:' + n.id, n.synopsis) }
              : {}),
            choices: n.choices.map((choice) => ({
              ...choice,
              label: replace('scene:' + n.id, choice.label),
            })),
          }
        : {}),
    })),
  }
}
export function removeBibleTarget(
  project: StoryProject,
  id: string,
): StoryProject {
  const bible = project.bible || blankBible()
  return {
    ...project,
    bible: {
      ...bible,
      characters: bible.characters.filter((c) => 'character:' + c.id !== id),
      entries: (bible.entries || []).filter((e) => 'entry:' + e.id !== id),
      links: (bible.links || []).filter(
        (l) => l.sourceId !== id && l.targetId !== id,
      ),
      ...(bible.parents ? { parents: bible.parents.filter(p => p.childId !== id && p.parentId !== id) } : {}),
    },
  }
}
export function safeReferenceUrl(value: string) {
  try {
    const url = new URL(value)
    return ['https:', 'http:'].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? url.href
      : null
  } catch {
    return null
  }
}
export function normalizeBible(
  raw: StoryBible,
  nodeIds: Map<string, string>,
): StoryBible {
  const string = (v: unknown) => (typeof v === 'string' ? v : '')
  const ids = <T extends { id: string }>(items: T[], label: string) => {
    if (
      items.some((i) => !i || typeof i.id !== 'string' || !i.id) ||
      new Set(items.map((i) => i.id)).size !== items.length
    )
      throw new Error(`${label} contains invalid or duplicate IDs.`)
    return items
  }
  const media = (item: {
    images?: BibleImage[]
    audio?: BibleAudio[]
    references?: BibleReference[]
  }) => ({
    ...(item.images
      ? {
          images: ids(item.images, 'Images').map((i) => {
            const details = { id: i.id, name: string(i.name), alt: string(i.alt) }
            if (i.dataUrl === undefined) {
              if (!/^[a-f0-9]{64}$/.test(i.assetId || '') || !['image/png', 'image/jpeg', 'image/webp'].includes(i.mimeType || '') ||
                !Number.isSafeInteger(i.size) || i.size! < 3 || i.size! > 1_050_000)
                throw new Error('A bible picture has an invalid file reference.')
              return { ...details, assetId: i.assetId, mimeType: i.mimeType, size: i.size }
            }
            if (!/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(i.dataUrl) || i.dataUrl.length > 1400100)
              throw new Error('A bible image is invalid or larger than 1 MB.')
            return { ...details, dataUrl: i.dataUrl }
          }),
        }
      : {}),
    ...(item.audio ? { audio: ids(item.audio, 'Audio recordings').map(audio => {
      if (!/^[a-f0-9]{64}$/.test(audio.assetId) || !['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/webm', 'audio/flac'].includes(audio.mimeType) ||
        !Number.isSafeInteger(audio.size) || audio.size < 16 || audio.size > 25_000_000)
        throw new Error('A bible recording is invalid or larger than 25 MB.')
      return { id: audio.id, name: string(audio.name).slice(0, 200), caption: string(audio.caption).slice(0, 1000), assetId: audio.assetId, mimeType: audio.mimeType, size: audio.size }
    }) } : {}),
    ...(item.references
      ? {
          references: ids(item.references, 'References').map((r) => {
            if (!safeReferenceUrl(r.url))
              throw new Error(
                'A bible reference needs a complete http or https address.',
              )
            return { id: r.id, label: string(r.label), url: r.url }
          }),
        }
      : {}),
  })
  const characters: StoryCharacter[] = ids(
    raw.characters || [],
    'Characters',
  ).map((c) => ({
    id: c.id,
    name: string(c.name),
    role: string(c.role),
    description: string(c.description),
    voice: string(c.voice),
    ...media(c),
  }))
  const sections = raw.sections
    ? ids(raw.sections, 'Bible sections').map((s) => ({
        id: s.id,
        title: string(s.title),
      }))
    : undefined
  const entries = raw.entries
    ? ids(raw.entries, 'Bible entries').map((e) => {
        if (
          e.sectionId !== 'locations' &&
          !sections?.some((s) => s.id === e.sectionId)
        )
          throw new Error('A bible entry refers to a missing section.')
        return {
          id: e.id,
          sectionId: e.sectionId,
          title: string(e.title),
          summary: string(e.summary),
          body: string(e.body),
          ...media(e),
        }
      })
    : undefined
  const targets = new Set(
    [...nodeIds.values()]
      .map((id) => 'scene:' + id)
      .concat(
        characters.map((c) => 'character:' + c.id),
        (entries || []).map((e) => 'entry:' + e.id),
      ),
  )
  const mapped = (id: string) =>
    id.startsWith('scene:')
      ? 'scene:' + (nodeIds.get(id.slice(6)) || id.slice(6))
      : id
  const links: BibleLink[] | undefined = raw.links
    ? ids(raw.links, 'Connections').map((l) => {
        const sourceId = mapped(l.sourceId),
          targetId = mapped(l.targetId)
        if (
          !targets.has(sourceId) ||
          !targets.has(targetId) ||
          sourceId === targetId
        )
          throw new Error(
            'A bible connection refers to a missing or identical entry.',
          )
        return {
          id: l.id,
          sourceId,
          targetId,
          relationship: string(l.relationship),
        }
      })
    : undefined
  const parents = raw.parents?.map(p => ({ childId: mapped(p.childId), parentId: mapped(p.parentId) }))
  if (parents) {
    const byChild = new Map<string, string>()
    for (const p of parents) {
      if (!targets.has(p.childId) || !targets.has(p.parentId) || p.childId === p.parentId || byChild.has(p.childId))
        throw new Error('Each entry needs one valid parent outside itself.')
      byChild.set(p.childId, p.parentId)
    }
    for (const child of byChild.keys()) {
      const seen = new Set([child])
      let parent = byChild.get(child)
      while (parent) {
        if (seen.has(parent)) throw new Error('The writing hierarchy cannot contain a cycle.')
        seen.add(parent)
        parent = byChild.get(parent)
      }
    }
  }
  return {
    premise: string(raw.premise),
    world: string(raw.world),
    notes: string(raw.notes),
    characters,
    ...(sections ? { sections } : {}),
    ...(entries ? { entries } : {}),
    ...(links ? { links } : {}),
    ...(parents ? { parents } : {}),
  }
}

// The editor and backups retain mention markers. Player/review output resolves
// only references that actually have a structured connection in this scene.
export function projectForReaders(project: StoryProject): StoryProject {
  const links = project.bible?.links || []
  if (!links.length) return project
  const match = createReferenceMatcher(project)
  const sources = new Set(links.map((link) => link.sourceId))
  return {
    ...project,
    nodes: project.nodes.map((node) => {
      if (!sources.has('scene:' + node.id)) return node
      const resolve = (text: string) =>
        replaceMentions(
          text,
          match('scene:' + node.id, text),
          (mention) => mention.title,
        )
      return {
        ...node,
        blocks: node.blocks.map((block) => ({
          ...block,
          text: resolve(block.text),
          ...(block.direction !== undefined
            ? { direction: resolve(block.direction) }
            : {}),
        })),
        choices: node.choices.map((choice) => ({
          ...choice,
          label: resolve(choice.label),
        })),
      }
    }),
  }
}
